import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Chip,
    Avatar,
    IconButton,
    Pagination,
    Paper,
    Divider,
    CircularProgress,
    Card,
    CardContent,
    Grid,
    useMediaQuery
} from '@mui/material';
import ReportHistoryDisplay from './ReportHistoryDisplay';
import {
    Timeline,
    TimelineItem,
    TimelineOppositeContent,
    TimelineSeparator,
    TimelineDot,
    TimelineConnector,
    TimelineContent
} from '@mui/lab';
import {
    Close as CloseIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Share as ShareIcon,
    Delete as DeleteIcon,
    Schedule as ScheduleIcon,
    Person as PersonIcon,
  
    History as HistoryIcon,
    AccessTime
} from '@mui/icons-material';
import { utcDisplay } from '../../utilis/date';
// import _ from 'lodash';
import * as reportAuditService from '../../services/report-audit-log.service';

const getActionIcon = (action) => {
    const iconProps = { fontSize: 'small' };
    switch (action) {
        case 'CREATED': return <AddIcon {...iconProps} />;
        case 'UPDATED': 
        case 'NAME_CHANGED':
        case 'INTERVAL_CHANGED':
        case 'FACILITIES_CHANGED': return <EditIcon {...iconProps} />;
        case 'DELETED': return <DeleteIcon {...iconProps} />;
        case 'SHARED': return <ShareIcon {...iconProps} />;
        case 'ACTIVATED': 
        case 'DEACTIVATED':
        case 'FACILITY_TOGGLE':
        case 'STATUS_TOGGLED': return <ScheduleIcon {...iconProps} />;
        default: return <HistoryIcon {...iconProps} />;
    }
};

const getActionColor = (action) => {
    switch (action) {
        case 'CREATED': return 'success';
        case 'UPDATED':
        case 'NAME_CHANGED':
        case 'INTERVAL_CHANGED':
        case 'FACILITIES_CHANGED': return 'primary';
        case 'DELETED': return 'error';
        case 'SHARED': return 'info';
        case 'ACTIVATED': return 'success';
        case 'DEACTIVATED': return 'warning';
        case 'STATUS_TOGGLED': return 'primary';
        case 'FACILITY_TOGGLE': return 'primary';
        default: return 'default';
    }
};

const ReportHistoryDialog = ({ open, onClose, reportId, reportName }) => {
    const [auditLogs, setAuditLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summary, setSummary] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);
    const isMobile = useMediaQuery('(max-width:768px)');

      useEffect(() => {
    if (open && reportId) {
      fetchAuditLogs();
      fetchAuditSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reportId, page]);

    const fetchAuditLogs = async () => {
        try {
            setLoading(true);
            const response = await reportAuditService.getReportAuditHistory(reportId, page, 20);

            if (response) {
                // The API returns { status: 200, data: { logs: [], total: ..., totalPages: ... } }
                // The interceptor returns response.data, so we get { status: 200, data: {...} }
                const data = response.data || response;
                setAuditLogs(data.logs || []);
                setTotalPages(data.totalPages || 1);
                setTotalLogs(data.total || 0);
            }
        } catch (error) {
            console.error('Error fetching audit logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAuditSummary = async () => {
        try {
            setSummaryLoading(true);
            const response = await reportAuditService.getReportAuditSummary(reportId);

            if (response) {
                // The API returns { status: 200, data: {...} }
                // The interceptor returns response.data, so we get { status: 200, data: {...} }
                const data = response.data || response;
                setSummary(data);
            }
        } catch (error) {
            console.error('Error fetching audit summary:', error);
        } finally {
            setSummaryLoading(false);
        }
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const formatDate = (date) => {
        if (!date) return '';
        return utcDisplay(date, 'MMM DD, YYYY • HH:mm');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    minHeight: isMobile ? 'none' : '70vh',
                    maxHeight: isMobile ? 'none' : '90vh'
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                pb: 1,
                px: { xs: 2, sm: 3 }
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon color="primary" />
                    <Box>
                        <Typography variant="h6" component="div">
                            Report History
                        </Typography>
                        <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                wordBreak: 'break-word'
                            }}
                        >
                            {reportName}
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    edge="end"
                    color="inherit"
                    onClick={onClose}
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ px: 0 }}>
                {/* Summary Section */}
                {summaryLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : summary && (
                    <Box sx={{ px: { xs: 2, sm: 3 }, mb: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined" sx={{ height: '100%' }}>
                                    <CardContent sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <PersonIcon sx={{ fontSize: 16 }} />
                                            Creator Information
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Avatar sx={{ width: 32, height: 32, fontSize: 14, backgroundColor: '#1976d2' }}>
                                                {summary.creator?.fullName?.[0] || 'U'}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {summary.creator?.fullName || 'Unknown'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Created {summary.createdAt ? formatDate(summary.createdAt) : 'Unknown'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {summary.lastModifiedBy && (
                                            <Box sx={{ mt: 1 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Last modified by {summary.lastModifiedBy.fullName}
                                                </Typography>
                                                <br />
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDate(summary.lastModifiedAt)}
                                                </Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined" sx={{ height: '100%' }}>
                                    <CardContent sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <AccessTime sx={{ fontSize: 16 }} />
                                            Report Description
                                        </Typography>
                                        {/* Use the new ReportHistoryDisplay component */}
                                        <ReportHistoryDisplay 
                                            description={summary.reportSubscription?.description} 
                                            isActive={summary.reportSubscription?.isActive}
                                            type="REPORT"
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                <Divider />

                {/* History Timeline */}
                <Box sx={{ px: { xs: 1.5, sm: 3 }, py: 2 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : auditLogs.length === 0 ? (
                        <Box sx={{ textAlign: 'center', p: 4 }}>
                            <Typography variant="body1" color="text.secondary">
                                No audit history found for this report.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Total activities: {totalLogs}
                                </Typography>
                                <Chip 
                                    label={`Page ${page} of ${totalPages}`} 
                                    size="small" 
                                    variant="outlined" 
                                />
                            </Box>
                            
                            <Timeline sx={{ p: isMobile ? 0 : undefined, '& .MuiTimelineItem-root:before': isMobile ? { display: 'none' } : undefined }}>                                {auditLogs.map((log, index) => (
                                    <TimelineItem key={log._id}>
                                        {!isMobile && (
                                            <TimelineOppositeContent sx={{ flex: 0.3, pr: 2 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDate(log.timestamp)}
                                                </Typography>
                                                {log.targetUser && (
                                                    <Box sx={{ mt: 0.5 }}>
                                                        <Chip 
                                                            size="small" 
                                                            label={log.targetUser.fullName}
                                                            variant="outlined"
                                                            sx={{ fontSize: '0.7rem' }}
                                                        />
                                                    </Box>
                                                )}
                                            </TimelineOppositeContent>
                                        )}
                                        
                                        <TimelineSeparator>
                                            <TimelineDot color={getActionColor(log.action)} variant="outlined">
                                                {getActionIcon(log.action)}
                                            </TimelineDot>
                                            {index < auditLogs.length - 1 && <TimelineConnector />}
                                        </TimelineSeparator>
                                        
                                        <TimelineContent sx={{ flex: 1, px: isMobile ? 1 : 2 }}>
                                            <Paper 
                                                elevation={1} 
                                                sx={{ 
                                                    p: isMobile ? 1.5 : 2, 
                                                    border: '1px solid #e0e0e0',
                                                    '&:hover': {
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                                    }
                                                }}
                                            >
                                                {isMobile && (
                                                    <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                                {formatDate(log.timestamp)}
                                                            </Typography>
                                                            {log.targetUser && (
                                                                <Chip 
                                                                    size="small" 
                                                                    label={`Target: ${log.targetUser.fullName}`}
                                                                    variant="outlined"
                                                                    sx={{ fontSize: '0.65rem', height: 18 }}
                                                                />
                                                            )}
                                                        </Box>
                                                        <Chip 
                                                            size="small" 
                                                            label={log.action}
                                                            color={getActionColor(log.action)}
                                                            variant="outlined"
                                                            sx={{ fontSize: '0.65rem', fontWeight: 500, height: 18 }}
                                                        />
                                                    </Box>
                                                )}
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                                            {log.description || log.actionDescription}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                            <Avatar sx={{ width: 24, height: 24, fontSize: 12, backgroundColor: '#1976d2' }}>
                                                                {log.user?.name?.[0] || log.user?.fullName?.[0] || <PersonIcon sx={{ fontSize: 12 }} />}
                                                            </Avatar>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {log.user?.name || log.user?.fullName || 'Unknown User'}
                                                            </Typography>
                                                        </Box>
                                                        
                                                        {/* Only show details if there are meaningful details beyond just the description */}
                                                        {log.details && Object.keys(log.details).length > 0 && !(Object.keys(log.details).length === 1 && log.details.description) && (
                                                            <Box
                                                                sx={{
                                                                    mt: 1,
                                                                    p: 1.5,
                                                                    backgroundColor: '#f8f9fa',
                                                                    borderRadius: 1,
                                                                    border: '1px solid #e9ecef'
                                                                }}
                                                            >
                                                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                                    Additional Details:
                                                                </Typography>
                                                                {Object.entries(log.details).filter(([key]) => key !== 'description').map(([key, value]) => (
                                                                    <Typography key={key} variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                                                        <strong>{key}:</strong> {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                                                    </Typography>
                                                                ))}
                                                            </Box>
                                                        )}
 
                                                        {log.ipAddress && (
                                                            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                                                                IP: {log.ipAddress}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    {!isMobile && (
                                                        <Chip 
                                                            size="small" 
                                                            label={log.action}
                                                            color={getActionColor(log.action)}
                                                            variant="outlined"
                                                            sx={{ ml: 1, fontSize: '0.7rem', fontWeight: 500 }}
                                                        />
                                                    )}
                                                </Box>
                                            </Paper>
                                        </TimelineContent>
                                    </TimelineItem>
                                ))}
                            </Timeline>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <Box 
                                    sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'center', 
                                        mt: 3,
                                        pb: isMobile ? 6 : 2,
                                        position: 'relative',
                                        zIndex: 5
                                    }}
                                >
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={handlePageChange}
                                        color="primary"
                                        variant="outlined"
                                        shape="rounded"
                                        size="medium"
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </DialogContent>

            {!isMobile && (
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={onClose} variant="outlined">
                        Close
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default ReportHistoryDialog; 